import moment, {type Moment} from "moment-timezone";

import {formatDate, SAFELISTING_COVERAGE_FORMATS as F} from "@/src/modules/core/lib/formatters";

export type PermanentPermitCoverageStatus = "scheduled" | "active" | "expired";

export function getPermanentPermitCoverageStatus(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  timeZone?: string,
): PermanentPermitCoverageStatus {
  const tz = timeZone?.trim() || moment.tz.guess();
  const now = moment.tz(tz).startOf("day");
  const start = startDate?.trim() ? parsePermitDate(startDate, tz) : null;
  const end = endDate?.trim() ? parsePermitDate(endDate, tz) : null;

  if (end && end.isBefore(now)) return "expired";
  if (start && start.isAfter(now)) return "scheduled";
  return "active";
}

export type PermanentPermitCoverage = {
  dayCount: number;
  daysLabel: string;
  overallRangeLabel: string;
  overallCompactLabel: string;
  /** Relative start hint for scheduled permits (Today, Tomorrow, Starts MMM D). */
  startHumanLabel: string | null;
};

function parsePermitDate(iso: string, timeZone: string): Moment {
  return moment.tz(iso.trim(), "YYYY-MM-DD", timeZone).startOf("day");
}

function countInclusiveDays(start: Moment, end: Moment): number {
  return Math.max(0, end.diff(start, "days") + 1);
}

export function getPermanentPermitStartHumanLabel(
  startDate: string | null | undefined,
  timeZone: string,
  now: Moment = moment.tz(timeZone).startOf("day"),
): string | null {
  const trimmed = startDate?.trim();
  if (!trimmed) return null;

  const start = parsePermitDate(trimmed, timeZone);
  if (!start.isAfter(now)) return null;

  if (start.isSame(now.clone().add(1, "day"), "day")) return "Tomorrow";
  if (start.isSame(now.clone().add(2, "day"), "day")) return "Day after";
  if (start.diff(now, "days") <= 7) return start.format("dddd");

  return `Starts ${start.format(F.compactDate)}`;
}

export function computePermanentPermitCoverage(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  timeZone?: string,
): PermanentPermitCoverage | null {
  const startRaw = startDate?.trim();
  const endRaw = endDate?.trim();
  if (!startRaw && !endRaw) return null;

  const tz = timeZone?.trim() || moment.tz.guess();
  const start = startRaw ? parsePermitDate(startRaw, tz) : null;
  const end = endRaw ? parsePermitDate(endRaw, tz) : null;

  if (!start && !end) return null;

  const effectiveStart = start ?? end!;
  const effectiveEnd = end ?? start!;
  const dayCount = countInclusiveDays(effectiveStart, effectiveEnd);
  const daysLabel = dayCount === 1 ? "1 day" : `${dayCount} days`;

  const startLabel = start ? formatDate(startRaw!) : "—";
  const endLabel = end ? formatDate(endRaw!) : "—";
  const overallRangeLabel = `${startLabel} → ${endLabel}`;

  const compactStart = start ? start.format(F.compactDate) : "—";
  const compactEnd = end ? end.format(F.compactDate) : "—";
  const overallCompactLabel =
    dayCount === 1
      ? compactStart
      : start?.isSame(end, "year")
        ? `${compactStart} – ${compactEnd}, ${effectiveEnd.format("YYYY")}`
        : `${compactStart} – ${compactEnd}`;

  return {
    dayCount,
    daysLabel,
    overallRangeLabel,
    overallCompactLabel,
    startHumanLabel: getPermanentPermitStartHumanLabel(startRaw, tz),
  };
}
